/**
 * React-Query hooks for the salesforce-release backend.
 *
 * org_id + user_id are resolved from the auth store on every call — there are
 * NO hardcoded ids anywhere. Components just call these hooks and inherit the
 * logged-in user's context.
 */
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/react/shallow";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { useOrg } from "@/hooks/org/useOrg";
import { useReleasePipelineStore } from "@/stores/releasePipelineStore";
import {
  cancelIngest,
  deleteReleaseDoc,
  getFindings,
  getOrgCoverage,
  listOrgMetadataTypes,
  listReleaseDocs,
  uploadReleaseDoc,
  type FindingOut,
  type FindingsFilterArgs,
  type FindingsResponse,
  type ImpactBucket,
  type OrgCoverage,
  type ReleaseDocument,
} from "@/services/salesforce-release";

// Backend rejects page_size > 200 (422) — the largest page we're allowed to ask for.
const MAX_FINDINGS_PAGE_SIZE = 200;

function useOrgId(): string | null {
  const { selectedOrg } = useOrg();
  return selectedOrg?.id ?? null;
}

function useUserId(): string | null {
  return useAuthStoreV1((state) => state.user?.id ?? null);
}

/** List uploaded release docs for the logged-in user's selected org.
 *  Auto-refetches every 4s while any doc is still processing. */
export function useReleaseDocsList() {
  const orgId = useOrgId();
  return useQuery<ReleaseDocument[]>({
    queryKey: ["salesforce-release", "documents", orgId],
    queryFn: () => listReleaseDocs(orgId!),
    enabled: Boolean(orgId),
    // 30s stale time — list only needs refreshing after upload/delete/cancel,
    // which each call invalidateQueries explicitly. No active polling needed
    // because pipeline progress is tracked via the SSE stream, not doc status.
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/** Upload a release doc; returns the created release_id. */
export function useUploadReleaseDoc() {
  const orgId = useOrgId();
  const userId = useUserId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      if (!orgId) throw new Error("no_selected_org");
      return uploadReleaseDoc(file, orgId, userId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesforce-release", "documents", orgId] });
    },
  });
}

/** Delete a release doc (cascade-deletes chunks + scans). */
export function useDeleteReleaseDoc() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => deleteReleaseDoc(releaseId, orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesforce-release", "documents", orgId] });
    },
  });
}

/** Org coverage snapshot for the unified metadata index — how many components
 *  are indexed and at what depth (code > content > class_summary > name_only). */
export function useOrgCoverage() {
  const orgId = useOrgId();
  return useQuery<OrgCoverage>({
    queryKey: ["salesforce-release", "metadata-coverage", orgId],
    queryFn: () => getOrgCoverage(orgId!),
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

/** Full list of metadata types present in the org's `metadata_items` table.
 *  Stable per org, so cache aggressively. Used to populate the component-type
 *  filter dropdown so users can drill into ANY type, not just the ones with
 *  findings in the current scan. */
export function useOrgMetadataTypes() {
  const orgId = useOrgId();
  return useQuery<string[]>({
    queryKey: ["salesforce-release", "metadata-types", orgId],
    queryFn: () => listOrgMetadataTypes(orgId!),
    enabled: Boolean(orgId),
    staleTime: 5 * 60 * 1000, // 5 minutes — metadata schema rarely changes mid-session
  });
}

/** Cancel an in-flight ingest (server flips status=cancelled; pipeline bails at next checkpoint). */
export function useCancelIngest() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (releaseId: string) => cancelIngest(releaseId, orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salesforce-release", "documents", orgId] });
    },
  });
}

const TERMINAL_DOC_STATUSES = new Set<string>(["ready", "failed", "cancelled"]);

/** Subscribe to the unified pipeline SSE for a release (covers document
 *  processing → scan queuing → scan running → completion in one stream).
 *  Pushes progress into the global ActivityStore.
 *
 *  The SSE connection is owned by the persistent Zustand releasePipelineStore,
 *  so it survives route changes — progress is visible in ActivityDropdown even
 *  when the user navigates away from /salesforce-releases.
 *
 *  - Does NOT set isActive until the first non-terminal SSE state event arrives,
 *    so already-completed docs never show the cancel button.
 *  - Watches document status from cache: if it flips to a terminal value (after
 *    cancel or completion), isActive resets without waiting for an SSE event.
 */
export function usePipelineStream(releaseId: string | null) {
  const orgId = useOrgId();
  const qc = useQueryClient();

  // Shallow selector: re-render only when actual state values change.
  // Without this, every SSE event (≥1/s during processing) re-renders
  // every component that calls usePipelineStream.
  const pipelineState = useReleasePipelineStore(useShallow((s) => s.state));
  const forceDeactivate = useReleasePipelineStore((s) => s.forceDeactivate);
  const startStream = useReleasePipelineStore((s) => s.startStream);

  // Watch doc status from the cached documents list so cancel/completion
  // immediately resets isActive even if the SSE stream is slow to emit.
  const docs = qc.getQueryData<ReleaseDocument[]>(["salesforce-release", "documents", orgId]);
  const currentDocStatus = docs?.find((d) => d.release_id === releaseId)?.status ?? null;

  // If the document is already terminal, ensure isActive is off regardless of stream state.
  useEffect(() => {
    if (releaseId && currentDocStatus && TERMINAL_DOC_STATUSES.has(currentDocStatus)) {
      forceDeactivate(releaseId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDocStatus, releaseId]);

  useEffect(() => {
    if (!releaseId || !orgId) return;

    // If the doc is already in a terminal state, skip opening the stream.
    const cachedDocs = qc.getQueryData<ReleaseDocument[]>(["salesforce-release", "documents", orgId]);
    const docStatus = cachedDocs?.find((d) => d.release_id === releaseId)?.status ?? null;
    if (docStatus && TERMINAL_DOC_STATUSES.has(docStatus)) {
      forceDeactivate(releaseId);
      return;
    }

    startStream(releaseId, orgId);
    // No cleanup — the store persists across unmounts so the SSE stream
    // keeps running when the user navigates away.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseId, orgId]);

  // Return state from the persistent store, falling back to defaults when the
  // store is tracking a different release (e.g. immediately after navigation).
  if (pipelineState.releaseId !== releaseId) {
    return { stage: null, progress: 0, elapsedSeconds: 0, error: null, isActive: false, closed: false };
  }

  return {
    stage: pipelineState.stage,
    progress: pipelineState.progress,
    elapsedSeconds: pipelineState.elapsedSeconds,
    error: pipelineState.error,
    isActive: pipelineState.isActive,
    closed: pipelineState.closed,
  };
}

export interface FindingsFilters {
  bucket?: ImpactBucket;
  componentType?: string;
  componentTypes?: string[];
  page?: number;
  pageSize?: number;
}

/** Fetch paginated findings from GET /findings for a release.
 *  Enabled only when releaseId is set. scanId from the response is used for exports. */
export function useFindings(releaseId: string | null, filters: FindingsFilters = {}) {
  const orgId = useOrgId();

  const bucketKey = filters.bucket ?? null;
  const typeKey = filters.componentType ?? null;
  const typesKey = filters.componentTypes?.length
    ? [...filters.componentTypes].sort().join(",")
    : null;
  const pageKey = filters.page ?? null;
  const pageSizeKey = filters.pageSize ?? null;

  const query = useQuery<FindingsResponse>({
    queryKey: ["salesforce-release", "findings", orgId, releaseId, bucketKey, typeKey, typesKey, pageKey, pageSizeKey],
    queryFn: () => {
      const f: FindingsFilterArgs = {
        bucket: filters.bucket,
        componentType: filters.componentType,
        componentTypes: filters.componentTypes,
        page: filters.page,
        pageSize: filters.pageSize,
      };
      return getFindings(orgId!, releaseId!, f);
    },
    enabled: Boolean(orgId && releaseId),
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });

  return {
    findings: query.data?.findings ?? [],
    summary: query.data?.summary ?? null,
    scanId: query.data?.scan_id ?? null,
    availableFilters: query.data?.summary?.available_filters ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
  };
}

/** Fetch EVERY finding for a release, unfiltered — used for scan-wide aggregates
 *  like the severity heatmap, where a single paginated page isn't representative.
 *  The backend caps page_size at 200, so this pages through in parallel and
 *  merges the results. Stays disabled until `totalFindings` is known (from the
 *  paginated `useFindings` summary) so we know how many pages to request. */
export function useAllFindings(releaseId: string | null, totalFindings: number) {
  const orgId = useOrgId();
  const pageCount = totalFindings > 0 ? Math.ceil(totalFindings / MAX_FINDINGS_PAGE_SIZE) : 0;

  const query = useQuery<FindingOut[]>({
    queryKey: ["salesforce-release", "findings-all", orgId, releaseId, totalFindings],
    queryFn: async () => {
      const pages = await Promise.all(
        Array.from({ length: pageCount }, (_, i) =>
          getFindings(orgId!, releaseId!, { page: i + 1, pageSize: MAX_FINDINGS_PAGE_SIZE }),
        ),
      );
      return pages.flatMap((p) => p.findings);
    },
    enabled: Boolean(orgId && releaseId && pageCount > 0),
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });

  return {
    findings: query.data ?? [],
    isLoading: query.isLoading,
  };
}

export type {
  ImpactBucket,
  FindingsResponse,
  PipelineStage,
  ReleaseDocument,
} from "@/services/salesforce-release";

export { useReleaseReportCsv, useReleaseReportJson } from "./useReleaseReport";
export { useOngoingReleaseJob } from "./useOngoingReleaseJob";
export type { OngoingReleaseJob } from "@/services/salesforce-release";
